import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { appEvents } from '@/lib/app-events';
import { notify } from '@/lib/notify';

/**
 * Two-tier feedback for backend 5xx errors — ROUTE_INVENTORY Part 1, G8.
 *
 *   1. **Every** `server-error` raises a deduped toast (`notify.error` keys it
 *      on `err:<status>:<message>`, so a hundred failures are one toast).
 *   2. **3+ inside 30s** raises the service-issues strip: a long burst means
 *      real trouble, not a blip.
 *
 * The strip clears after 30s of quiet. Dismissing it holds for the rest of
 * that burst - every 5xx used to reset the dismissal, so during an outage the
 * ✕ lasted a second or two - and the quiet-period reset lets a NEW outage show
 * it again. Suppressed on `/auth`; errors there still count.
 */
const BURST_THRESHOLD = 3;
const BURST_WINDOW_MS = 30_000;

export interface ServiceIssuesBannerState {
  raised: boolean;
  dismissed: boolean;
  dismiss: () => void;
}

export function useServiceIssuesBanner(): ServiceIssuesBannerState {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const recent = useRef<number[]>([]);
  const clearTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();

  useEffect(() => {
    const off = appEvents.on('server-error', event => {
      const now = Date.now();
      recent.current = recent.current.filter(t => now - t < BURST_WINDOW_MS);
      recent.current.push(now);

      notify.error(
        { name: 'ApiError', status: event.status, detail: '' },
        'Something went wrong on our end.'
      );

      if (recent.current.length >= BURST_THRESHOLD) {
        setVisible(true);
        if (clearTimer.current) clearTimeout(clearTimer.current);
        clearTimer.current = setTimeout(() => {
          setVisible(false);
          setDismissed(false);
          recent.current = [];
        }, BURST_WINDOW_MS);
      }
    });
    return () => {
      off();
      if (clearTimer.current) clearTimeout(clearTimer.current);
    };
  }, []);

  return {
    raised: visible && !location.pathname.startsWith('/auth'),
    dismissed,
    dismiss: () => setDismissed(true),
  };
}
